import type { ReactNode, Ref } from "react";

/**
 * Les briques de mise en forme communes aux cartes du module Cadres, aux cotes des maquettes
 * `Cadres*.dc.html` (carte a 20 px de marge, titre a 15 px, texte discret a 13 px). Les
 * classes sont celles de la page Silhouettes, qui suit la meme famille de maquettes.
 */

export const CARTE = "min-w-0 rounded-lg border border-border bg-card p-5 text-card-foreground";

/** La note a filet gris de la maquette (`.note`). */
export const NOTE = "border-l-[3px] border-border pl-3 text-[13.5px] text-muted-foreground";

/** Les trois encadres de la maquette (`.alerte`) : avertissement, refus, panne. */
export const ALERTE = {
  jaune: "rounded-md border border-avertissement/60 bg-avertissement/15 px-3.5 py-2.5 text-sm",
  rouge:
    "rounded-md border border-destructive/40 bg-destructive/[0.06] px-3.5 py-2.5 text-sm text-destructive",
  grise: "rounded-md border border-border bg-muted px-3.5 py-2.5 text-sm",
} as const;

/** Un anneau de focus visible, pose sur tous les controles faits main du module. */
export const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card";

/** Le petit bouton de contour de la maquette (`.btn` a 13 px). */
export const PETIT_BOUTON = `inline-flex h-8 items-center justify-center gap-2 rounded-md border border-input bg-card px-2.5 text-[13px] font-medium text-foreground hover:bg-muted ${FOCUS}`;

/** Sa forme eteinte, toujours focalisable (`aria-disabled`) : le focus ne se perd pas. */
export const PETIT_BOUTON_ETEINT =
  "inline-flex h-8 cursor-default items-center justify-center gap-2 rounded-md border border-border bg-muted/50 px-2.5 text-[13px] font-medium text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card";

/**
 * L'en-tete d'une carte : le titre a gauche, une precision discrete a droite, sur la meme
 * ligne de base (maquette : « Ordre des plaques » et « de l'avant vers le fond »).
 */
export function EnTete({
  id,
  titre,
  aparte,
  titreRef,
}: {
  id: string;
  titre: string;
  aparte?: ReactNode;
  /** Pour une carte qui reprend le focus sur son titre (`tabIndex={-1}`). */
  titreRef?: Ref<HTMLHeadingElement>;
}) {
  return (
    <div className="mb-3.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2
        className={`rounded-sm text-[15px] font-semibold ${FOCUS}`}
        id={id}
        ref={titreRef}
        tabIndex={titreRef ? -1 : undefined}
      >
        {titre}
      </h2>
      {aparte ? <div className="text-[13px] text-muted-foreground">{aparte}</div> : null}
    </div>
  );
}

/** Le texte long, et sous `md` le texte court de la maquette du telephone. */
export function LongOuCourt({ long, court }: { long: ReactNode; court: ReactNode }) {
  if (typeof long === "string" && long === court) return <>{long}</>;
  return (
    <>
      <span className="hidden md:inline">{long}</span>
      <span className="md:hidden">{court}</span>
    </>
  );
}
